import { createAsyncThunk } from '@reduxjs/toolkit';
import type { AuthenticationResponseJSON } from '@simplewebauthn/typescript-types';
import { AxiosError } from 'axios';

import ACCOUNT_CLIENT_API_ENDPOINTS from '@api/endpoints';
import HTTP_METHODS_ENUM from '@api/enums/HTTP_METHODS_ENUM.enum';
import httpClient from '@api/httpClient';
import CurrentSessionInterface from '@api/requests/auth/interfaces/CurrentSessionInterface.interface';
import CurrentUserInterface from '@api/requests/auth/interfaces/CurrentUserInterface.interface';

export interface LoginData {
	login: string;
	password: string;
	mfa_codes?: {
		email?: string;
		phone?: string;
		totp?: string;
		yubico_webauthn?: AuthenticationResponseJSON;
	};
	device_id: string;
	remember_me?: boolean;
}

export interface LoginResponse {
	phone_verification_needed: boolean;
	email_verification_needed: boolean;
	login_basic: {
		tokens: CurrentSessionInterface;
		user: CurrentUserInterface;
	};
	should_enable_mfa: boolean;
}

export interface LoginPayload {
	login_basic: {
		tokens: CurrentSessionInterface;
		user: CurrentUserInterface;
	};
}

const login = createAsyncThunk<
	LoginPayload,
	LoginData,
	{
		rejectValue: AxiosError;
	}
>(ACCOUNT_CLIENT_API_ENDPOINTS.AUTH.LOGIN(), async (data, { rejectWithValue }) => {
	try {
		const loginResponse = await httpClient.request<LoginResponse>({
			url: ACCOUNT_CLIENT_API_ENDPOINTS.AUTH.LOGIN(),
			method: HTTP_METHODS_ENUM.POST,
			data,
		});

		return {
			login_basic: {
				...loginResponse.data.login_basic,
				user: {
					...loginResponse.data.login_basic.user,
					phone_verification_needed: loginResponse.data.phone_verification_needed,
					email_verification_needed: loginResponse.data.email_verification_needed,
					should_enable_mfa: loginResponse.data.should_enable_mfa,
				},
			},
		};
	} catch (e: unknown) {
		return rejectWithValue(e as AxiosError);
	}
});

export default login;
